import type { CodeSummary, LlmStatus } from '../lib/metricsTypes';

interface Props {
  codes: readonly CodeSummary[];
}

const statusLabel: Record<LlmStatus, string> = {
  pending: 'pending',
  completed: 'done',
  failed: 'failed',
  cancelled: 'cancelled',
};

const statusClass: Record<LlmStatus, string> = {
  pending: 'text-gray-400',
  completed: 'text-emerald-400',
  failed: 'text-red-400',
  cancelled: 'text-amber-400',
};

/// Per-code roll-up rendered at Stop: decoded value, how many frames each
/// code was seen in, and the LLM-derived item count when available.
export function ResultsTable({ codes }: Props) {
  if (codes.length === 0) {
    return (
      <section
        aria-labelledby="cycle-count-results-heading"
        data-testid="cycle-count-results-table"
        className="rounded-lg bg-gray-900 p-4"
      >
        <h2
          id="cycle-count-results-heading"
          className="text-sm font-semibold uppercase tracking-wide text-gray-300"
        >
          Scanned codes
        </h2>
        <p className="mt-3 text-xs text-gray-500">
          No codes decoded during this run.
        </p>
      </section>
    );
  }

  const totalItems = codes.reduce(
    (sum, c) => sum + (c.itemCount ?? 0),
    0,
  );

  return (
    <section
      aria-labelledby="cycle-count-results-heading"
      data-testid="cycle-count-results-table"
      className="rounded-lg bg-gray-900 p-4"
    >
      <h2
        id="cycle-count-results-heading"
        className="text-sm font-semibold uppercase tracking-wide text-gray-300"
      >
        Scanned codes ({codes.length} unique • {totalItems} items counted)
      </h2>
      <table className="mt-3 w-full text-sm text-gray-200">
        <caption className="sr-only">
          Per-code results from this Cycle Count run
        </caption>
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
            <th scope="col" className="pb-2 pr-4">Code</th>
            <th scope="col" className="pb-2 pr-4 text-right">Items</th>
            <th scope="col" className="pb-2 pr-4 text-right">Frames</th>
            <th scope="col" className="pb-2">LLM</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {codes.map((c) => (
            <tr key={c.code} data-testid={`cycle-count-code-row-${c.code}`}>
              <td className="py-2 pr-4 font-mono text-xs break-all">{c.code}</td>
              <td className="py-2 pr-4 text-right tabular-nums">
                {c.itemCount ?? '—'}
              </td>
              <td className="py-2 pr-4 text-right tabular-nums text-gray-400">
                {c.frameCount}
              </td>
              <td className={`py-2 text-xs ${statusClass[c.llmStatus]}`}>
                {statusLabel[c.llmStatus]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
